import { useCallback } from "react";
import useContent from "../../../hooks/useContent";

// English copy until the managerAppInstallation entries "manager_app_x10_<key>" exist in Contentful.
export const X10_TEXT: { [key: string]: string } = {
  machines: "X10 machines",
  machine: "X10 machine",
  filters_all: "All",
  online: "Machines online",
  beer_on_tap: "Beer on tap",
  beer_on_tap_sub: "Across %COUNT% mounted kegs",
  average_temperature: "Average beer temperature",
  average_temperature_sub: "Recommended 0–5 °C",
  kegs_to_change: "Kegs to change",
  kegs_to_change_sub: "Almost empty, old or missing",
  ok: "All good",
  offline: "Offline",
  open: "Tower open",
  beverage_unknown: "Beverage not identified",
  no_keg: "No keg",
  volume_of: "%LEFT% / %TOTAL% L",
  day_of: "Day %DAYS% / %LIMIT%",
  last_seen: "Last seen %DATE%",

  headline_offline: "Offline",
  headline_offline_description:
    "No contact since %DATE%. Check that the machine is plugged in and within Wi-Fi range.",
  headline_open: "Tower open",
  headline_open_description:
    "The tower is open, usually for a keg change. Close it to resume cooling and pouring.",
  headline_leak: "Air leak detected",
  headline_leak_description:
    "The machine is losing pressure. Check that the keg is seated correctly and the tower is fully closed.",
  headline_keg_time_danger: "Replace the keg",
  headline_keg_time_danger_description:
    "This keg has been on tap for %DAYS% days, past the %LIMIT%-day quality limit.",
  headline_keg_time_warning: "Replace keg soon",
  headline_keg_time_warning_description:
    "This keg has been on tap for %DAYS% days. Beer quality drops after %LIMIT% days.",
  headline_expiration_danger: "Keg past its best-before date",
  headline_expiration_danger_description: "The best-before date was %DAYS% days ago.",
  headline_expiration_warning: "Best-before date soon",
  headline_expiration_warning_description: "The keg reaches its best-before date in %DAYS% days.",
  headline_temperature: "Beer too warm",
  headline_temperature_description: "The beer is at %TEMP%, above the recommended 0–5 °C.",
  headline_hardware: "Hardware fault",
  headline_hardware_description: "Reported by: %PARTS%. Contact support if it persists.",
  headline_hardware_generic_description:
    "The machine reported a hardware fault. Contact support if it persists.",
  headline_no_keg: "No keg mounted",
  headline_no_keg_description:
    "Mount a keg to start pouring. The beverage is read from the keg's NFC tag.",
  headline_empty: "Keg almost empty",
  headline_empty_description: "Less than 1 L left. Have a new keg ready.",
  headline_cooling_down: "Cooling down",
  headline_cooling_down_description:
    "The beer is at %TEMP% and cooling down, usually after a keg change.",
  headline_ok: "Ready to pour",
  headline_ok_description: "Keg, temperature and hardware are all within range.",

  time_on_keg: "Time on keg",
  time_on_keg_value: "Day %DAYS% of %LIMIT%",
  time_on_keg_ok: "%DAYS% days of best quality left",
  time_on_keg_warning: "%DAYS% days left, plan a keg change",
  time_on_keg_danger: "%DAYS% days over the limit, replace the keg",
  best_before: "Best before",
  best_before_in: "In %DAYS% days",
  best_before_ago: "%DAYS% days ago",
  beverage_tag: "Beverage tag",
  beverage_tag_missing: "No NFC tag",
  beverage_tag_missing_description: "Beverage set by staff, so there is no best-before date.",
  beverage_tag_unknown_description: "The keg's tag didn't match a known beverage.",
  temperature: "Temperature",
  temperature_vessel: "Beer",
  temperature_room: "Room",
  temperature_ok: "Within the recommended 0–5 °C",
  temperature_high: "Above the recommended 0–5 °C",
  temperature_cooling_down: "Cooling down",
  temperature_room_too_hot: "The room is too hot for the machine to cool the beer",
  temperature_boost: "Boost cooling on",
  hardware: "Hardware",
  hardware_ok: "OK",
  hardware_fault: "Fault reported",
  hardware_air_pump: "Air pump",
  hardware_fan: "Fan",
  hardware_refrigeration: "Refrigeration",
  hardware_pressure_sensor: "Pressure sensor",
  hardware_power_supply: "Power supply",
  details: "Machine details",
  serial_number: "Serial number",
  firmware: "Firmware",
  firmware_up_to_date: "Up to date",
  firmware_update: "Update to %VERSION% available",
  keg_mounted: "Keg mounted",
  last_pour: "Last pour",
  last_seen_label: "Last seen",
  installed: "Installed",
  read_at: "Read %DATE%",
  read_only: "Change beverages and settings from the X10 app.",
  volume_per_machine: "Volume per X10 machine",
  volume_per_machine_sub: "Change compared with the previous period of the same length",
  volume_per_machine_empty: "No X10 pours in this period.",
  show_all: "Show all %COUNT%",
  show_less: "Show fewer"
};

export type X10TextVars = { [name: string]: string | number | undefined };

export const formatX10Text = (template: string, vars: X10TextVars = {}) =>
  Object.entries(vars).reduce(
    (text, [name, value]) => text.split(`%${name}%`).join(value === undefined ? "" : `${value}`),
    template
  );

/** t("headline_ok") reads managerAppInstallation.manager_app_x10_headline_ok, falling back to English. */
const useX10Text = () => {
  const { managerAppInstallation: content } = useContent();

  return useCallback(
    (key: string, vars?: X10TextVars) =>
      formatX10Text(content?.[`manager_app_x10_${key}`] || X10_TEXT[key] || key, vars),
    [content]
  );
};

export default useX10Text;
